import { useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, PackageOpen, PackagePlus, User, Building2, MapPin, Mail,
  UserCheck, ClipboardList, CalendarClock, Clock, Truck, Download, Scale, Send, X,
} from "lucide-react";

/**
 * Reports · Reference Lookup Panel.
 * Type a Reference / Tracking / AWB number → full parcel dossier layout
 * (Shipper / Employee / Entry User / Consignee / Status) matching the
 * screenshot the customer shared. Also exposes direction-scoped Manifest downloads.
 *
 * Props:
 *   direction: "inward" | "outward"
 *   branchId: currently-scoped branch id (optional)
 */
export default function ReferenceLookupPanel({ direction, branchId }) {
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manifestDate, setManifestDate] = useState(new Date().toISOString().slice(0, 10));
  const [manifestBusy, setManifestBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", note: "" });

  const dirLabel = direction === "outward" ? "Outward" : "Inward";
  const DirIcon = direction === "outward" ? PackageOpen : PackagePlus;

  const doSearch = async (e) => {
    e?.preventDefault();
    const q = ref.trim();
    if (q.length < 3) return toast.error("Enter a full reference / AWB number");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/reports/lookup", {
        params: { ref: q, direction },
      });
      setResult(data);
      toast.success(`Loaded ${data.parcel?.tracking_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not found in your scope");
    } finally { setBusy(false); }
  };

  const downloadManifest = async (fmt) => {
    setManifestBusy(true);
    try {
      const { data } = await api.get(`/reports/document/daily/export`, {
        params: { format: fmt, date: manifestDate, direction, branch_id: branchId || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${direction}-manifest-${manifestDate}.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${dirLabel} manifest`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifest export failed");
    } finally { setManifestBusy(false); }
  };

  const openEmail = () => {
    if (!result?.parcel) return toast.error("Track a parcel first, then email the dossier");
    // Prefill with the counterparty most likely wanted for status update
    const prefill = direction === "outward"
      ? (result.parcel.recipient_email || result.parcel.sender_email || "")
      : (result.parcel.recipient_email || result.parcel.sender_email || "");
    setEmailForm({ to: prefill, note: "" });
    setEmailOpen(true);
  };

  const openManifestEmail = () => {
    setEmailForm({ to: "", note: "", _manifest: true });
    setEmailOpen(true);
  };

  const sendManifest = async () => {
    const to = (emailForm.to || "").trim();
    if (!to || !to.includes("@")) return toast.error("Enter a valid recipient email");
    setEmailBusy(true);
    try {
      const { data } = await api.post("/reports/email", {
        to, kinds: ["daily"], format: "pdf",
        date: manifestDate, direction, note: emailForm.note || "",
        branch_id: branchId || undefined,
      });
      toast.success(data.ok ? `Manifest emailed to ${data.sent_to}` : `Queued for ${to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifest email failed");
    } finally { setEmailBusy(false); }
  };

  const sendEmail = async () => {
    if (emailForm._manifest) return sendManifest();
    const to = (emailForm.to || "").trim();
    if (!to || !to.includes("@")) return toast.error("Enter a valid recipient email");
    setEmailBusy(true);
    try {
      const { data } = await api.post("/reports/lookup/email", {
        ref: result.parcel.tracking_number,
        direction,
        to,
        note: emailForm.note || "",
      });
      toast.success(data.ok ? `Emailed dossier to ${data.sent_to}` : `Queued for ${to} (email service unavailable)`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally { setEmailBusy(false); }
  };

  return (
    <section className="border border-border bg-card rounded-md" data-testid={`ref-lookup-${direction}`}>
      <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <DirIcon size={16} className="text-primary" />
          <div>
            <div className="overline">{dirLabel} manifest + status tracking</div>
            <div className="font-display text-xl mt-1">{dirLabel} — Reference lookup</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid={`manifest-date-${direction}`}
            type="date"
            value={manifestDate}
            onChange={(e) => setManifestDate(e.target.value)}
            className="h-9 rounded-md w-40 mono text-xs"
          />
          <Button size="sm" variant="outline" onClick={() => downloadManifest("pdf")} disabled={manifestBusy}
            data-testid={`manifest-pdf-${direction}`} className="rounded-md">
            <Download size={12} className="mr-1" /> Manifest PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadManifest("xlsx")} disabled={manifestBusy}
            data-testid={`manifest-xlsx-${direction}`} className="rounded-md">
            <Download size={12} className="mr-1" /> Excel
          </Button>
          <Button size="sm" onClick={openManifestEmail}
            data-testid={`manifest-email-${direction}`}
            className="rounded-md bg-primary text-primary-foreground">
            <Mail size={12} className="mr-1" /> Email manifest
          </Button>
        </div>
      </div>

      <form onSubmit={doSearch} className="p-5 border-b border-border flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={`ref-input-${direction}`}
            value={ref}
            onChange={(e) => setRef(e.target.value.toUpperCase())}
            placeholder={`Type ${dirLabel} Ref No or AWB (e.g. MR-ACME-260223-0001 or EA048636357IN)`}
            className="pl-9 h-11 rounded-md font-mono text-sm"
          />
        </div>
        <Button data-testid={`ref-search-${direction}`} type="submit" disabled={busy}
          className="h-11 rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Search size={14} className="mr-2" /> {busy ? "Searching…" : "Track"}
        </Button>
      </form>

      {result ? <DossierView data={result} direction={direction} onEmail={openEmail} /> : (
        <div className="p-8 text-center text-sm text-muted-foreground" data-testid={`ref-empty-${direction}`}>
          Type a reference or AWB above and press <b>Track</b> to view the full parcel dossier and current status.
        </div>
      )}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-md max-w-md" data-testid={`ref-email-dialog-${direction}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Mail size={16} className="text-primary" />
              {emailForm._manifest ? `Email ${dirLabel.toLowerCase()} manifest` : `Email ${dirLabel.toLowerCase()} dossier`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="border border-border rounded-md p-3 bg-muted/30">
              <div className="overline">{emailForm._manifest ? "Manifest for" : "Sending dossier for"}</div>
              <div className="font-mono font-bold mt-0.5">
                {emailForm._manifest ? `${dirLabel} · ${manifestDate}` : (result?.parcel?.tracking_number || "—")}
              </div>
              {!emailForm._manifest && (
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {result?.parcel?.courier} · {(result?.parcel?.status || "").replace(/_/g, " ")}
                </div>
              )}
            </div>
            <label className="block">
              <span className="overline">Send to</span>
              <Input
                data-testid={`ref-email-to-${direction}`}
                type="email"
                required
                value={emailForm.to}
                onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                placeholder="recipient@company.com"
                className="mt-1 rounded-md"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Pre-filled with the parcel's {direction === "outward" ? "consignee" : "consignor"} email if we have it. Change anytime.
              </div>
            </label>
            <label className="block">
              <span className="overline">Note (optional)</span>
              <Textarea
                data-testid={`ref-email-note-${direction}`}
                value={emailForm.note}
                onChange={(e) => setEmailForm((f) => ({ ...f, note: e.target.value }))}
                rows={3}
                placeholder="Add a short message · shown above the dossier"
                className="mt-1 rounded-md"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-md" data-testid={`ref-email-cancel-${direction}`}>
                <X size={13} className="mr-1" /> Cancel
              </Button>
              <Button
                onClick={sendEmail}
                disabled={emailBusy}
                className="rounded-md bg-primary text-primary-foreground"
                data-testid={`ref-email-send-${direction}`}
              >
                <Send size={13} className="mr-1" /> {emailBusy ? "Sending…" : "Send email"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function DossierView({ data, direction, onEmail }) {
  const p = data.parcel || {};
  const tenant = data.tenant || {};
  const branch = data.branch || {};
  const dEv = data.delivered_event;
  const zone = (() => {
    const t = (p.timeline || []).find((ev) => ev.status === "in_transit");
    return t?.remarks || p.destination_pincode?.slice(0, 3) || "—";
  })();

  // "Shipper" = consignor; "Consignee" = recipient
  const shipperCol = [
    ["Batch No", p.entry_mode === "Postal" ? "Postal Entry" : "Direct Entry"],
    ["Log Dt/Tm", fmtDT(p.created_at)],
    ["AWB No", p.awb_number || "—"],
    ["Mode", p.entry_mode || p.shipping_mode || "Courier"],
    ["NO of Pcs", p.item_count || 1],
    ["Actual Weight", p.actual_weight_kg != null ? `${p.actual_weight_kg} kg` : `${p.weight_kg || "—"} kg`],
    ["Volumetric Weight", p.volumetric_weight_kg != null ? `${p.volumetric_weight_kg} kg` : "0"],
    ["Chargeable Weight", p.chargeable_weight_kg != null ? `${p.chargeable_weight_kg} kg` : `${p.weight_kg || "—"} kg`],
    ["Zone", zone],
    ["Cost Center", tenant.code || tenant.name || "—"],
    ["Product", `${p.shipping_mode || "Air"} (${p.parcel_type || "Doc"})`],
    ["Log Remark", p.notes || p.parcel_type || "—"],
  ];

  const employeeCol = [
    ["Employee", direction === "outward" ? (p.sender_name || "—") : (p.recipient_name || "—")],
    ["Depart", p.recipient_department || "—"],
    ["Branch", branch.name || "—"],
    ["Route", branch.location || branch.name || "—"],
  ];

  const entryUserCol = [
    ["OSR No", p.tracking_number || "—"],
    ["Log Date Time", fmtDT(p.created_at)],
    ["Log User", p.created_by_name || "—"],
    ["Entry Branch", branch.name || "—"],
  ];

  const consigneeCol = [
    ["Consignee", direction === "outward" ? (p.recipient_name || "—") : (p.sender_name || "—")],
    ["City", `${p.destination_city || ""}${p.destination_pincode ? "-" + p.destination_pincode : ""}`.replace(/^-/, "") || "—"],
    ["Area", (direction === "outward" ? p.recipient_address : p.sender_address) || "—"],
    ["Phone No", (direction === "outward" ? p.recipient_phone : p.sender_phone) || "—"],
    ["Reference", p.awb_number || "NO"],
    ["Mail Content", p.parcel_type || "Letter"],
    ["Remark", p.notes || "—"],
  ];

  const statusCol = [
    ["Status", statusPretty(p.status)],
    ["Rec By", dEv?.by || "—"],
    ["Rec Dt/Tm", fmtDT(dEv?.at)],
    ["Manifest No", p.tracking_number ? `MFT-${p.tracking_number.slice(-8)}` : "—"],
  ];

  return (
    <div className="p-5 space-y-4" data-testid={`ref-dossier-${direction}`}>
      <div className="flex items-center justify-end -mb-2">
        <Button
          size="sm"
          onClick={onEmail}
          data-testid={`ref-email-open-${direction}`}
          className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"
        >
          <Mail size={13} className="mr-1.5" /> Email this dossier
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DossierBlock title="Shipper Details" icon={User} rows={shipperCol} />
        <div className="space-y-4">
          <DossierBlock title="Employee Details" icon={UserCheck} rows={employeeCol} />
          <DossierBlock title="Entry User Details" icon={ClipboardList} rows={entryUserCol} />
        </div>
        <div className="space-y-4">
          <DossierBlock title="Consignee Details" icon={Building2} rows={consigneeCol} />
          <DossierBlock title="Status details" icon={CalendarClock} rows={statusCol} highlight />
        </div>
      </div>

      {/* Timeline */}
      <div className="border border-border rounded-md bg-muted/30 p-4" data-testid={`ref-timeline-${direction}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="overline flex items-center gap-2"><Clock size={12} /> Status timeline</div>
          {data.external_tracking_url && (
            <a href={data.external_tracking_url} target="_blank" rel="noreferrer"
              className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
              <Truck size={11} /> Track on {p.courier}
            </a>
          )}
        </div>
        <div className="space-y-2">
          {(p.timeline || []).length === 0 && <div className="text-xs text-muted-foreground">No timeline events yet.</div>}
          {(p.timeline || []).map((ev, i) => (
            <div key={i} className="flex items-start gap-3 text-sm">
              <div className={`mt-1 h-2 w-2 rounded-full ${ev.status === "delivered" ? "bg-emerald-500" : "bg-primary"}`} />
              <div className="flex-1">
                <div className="font-semibold uppercase tracking-widest text-xs">{statusPretty(ev.status)}</div>
                <div className="text-[11px] text-muted-foreground">
                  {fmtDT(ev.at)} · by {ev.by || "system"} {ev.remarks ? `· ${ev.remarks}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost strip */}
      {(p.net_amount != null || p.gross_amount != null) && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={`ref-cost-${direction}`}>
          <Kpi label="Net" value={inr(p.net_amount)} />
          <Kpi label="Fuel surcharge" value={inr(p.fuel_surcharge)} />
          <Kpi label="GST @ 18%" value={inr(p.gst_amount)} />
          <Kpi label="Gross" value={inr(p.gross_amount)} highlight />
          {p.customs_value_inr != null && <Kpi label="Customs value" value={inr(p.customs_value_inr)} />}
        </div>
      )}
    </div>
  );
}

function DossierBlock({ title, icon: Icon, rows, highlight }) {
  return (
    <div className={`border rounded-md overflow-hidden ${highlight ? "border-primary" : "border-border"}`}>
      <div className={`px-3 py-2 flex items-center gap-2 font-display uppercase tracking-widest text-sm font-bold ${
        highlight ? "bg-primary text-primary-foreground" : "bg-primary text-primary-foreground"
      }`}>
        <Icon size={13} /> {title}
      </div>
      <div className="divide-y divide-border">
        {rows.map(([k, v], i) => (
          <div key={i} className="grid grid-cols-[110px_1fr] items-start px-3 py-1.5 text-[13px]">
            <div className="text-muted-foreground">{k}:</div>
            <div className="font-medium break-words">{v ?? "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight }) {
  return (
    <div className={`border rounded-md p-3 ${highlight ? "border-primary bg-red-50/60" : "border-border bg-muted/30"}`}>
      <div className="overline">{label}</div>
      <div className={`font-display text-lg font-mono ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return String(iso); }
}

function statusPretty(s) {
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function inr(n) {
  if (n == null || n === "") return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
